/**
 * AlertsTable Component
 *
 * Displays active alerts in an IndexTable with actions.
 * Supports resolve and dismiss actions with loading states.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  IndexTable,
  InlineStack,
  Box,
  Button,
  Tooltip,
  BlockStack,
  Text,
  Popover,
  ActionList,
} from '@shopify/polaris';
import {
  CheckIcon,
  XIcon,
  NotificationIcon,
  MenuHorizontalIcon,
} from '@shopify/polaris-icons';
import type { EnhancedAlert } from '../../types/dashboard';
import { ProductCell } from './ProductCell';
import { DataPill } from '../ui/DataPill';
import { getSeverityBadge } from './badge-utils';

// Re-export for backward compatibility with existing imports
export { getSeverityBadge } from './badge-utils';

/**
 * Props for AlertsTable component
 */
export interface AlertsTableProps {
  alerts: EnhancedAlert[];
  dismissingAlerts: Set<number>;
  resolvingAlerts: Set<number>;
  notifyingAlerts: Set<number>;
  onResolve: (alert: EnhancedAlert) => void;
  onDismiss: (alert: EnhancedAlert) => void;
  onNotify: (alert: EnhancedAlert) => void;
  /** Called when a row is clicked to show alert details */
  onRowClick?: (alert: EnhancedAlert) => void;
  /** Whether the shop has test data (used for test badge display) */
  hasTestData?: boolean;
  /** Whether notification channels are available */
  hasNotificationChannels?: boolean;
}

/**
 * Helper function to format timestamp as relative time
 * Note: Uses a passed-in 'now' parameter to avoid hydration mismatches
 */
function formatRelativeTime(dateString: string, now: Date): string {
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Helper function to format timestamp for tooltip display
 */
function formatFullTimestamp(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Check if a product title indicates test data
 */
function isTestProduct(title: string): boolean {
  return /\(Test\s*\d*\)/i.test(title);
}

/**
 * Active Alerts IndexTable component
 */
export function AlertsTable({
  alerts,
  dismissingAlerts,
  resolvingAlerts,
  notifyingAlerts,
  onResolve,
  onDismiss,
  onNotify,
  onRowClick,
  hasTestData = false,
  hasNotificationChannels = false,
}: AlertsTableProps) {
  // Track which row's action menu is open
  const [activePopoverId, setActivePopoverId] = useState<number | null>(null);

  const togglePopover = useCallback((alertId: number) => {
    setActivePopoverId((current) => (current === alertId ? null : alertId));
  }, []);

  const closePopover = useCallback(() => {
    setActivePopoverId(null);
  }, []);

  // Use state for current time to avoid hydration mismatch
  // Server renders with null, client updates after mount
  const [currentTime, setCurrentTime] = useState<Date | null>(null);

  useEffect(() => {
    setCurrentTime(new Date());
    // Update every minute to keep relative times fresh
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="animated-table alerts-table-active">
      <IndexTable
        resourceName={{ singular: 'alert', plural: 'alerts' }}
        itemCount={alerts.length}
        headings={[
          { title: 'Product' },
          { title: 'Created', alignment: 'center' },
          { title: 'Stock', alignment: 'center' },
          { title: 'Days Left', alignment: 'center' },
          { title: 'Velocity', alignment: 'center' },
          { title: 'Status', alignment: 'center' },
          { title: 'Actions', alignment: 'end' },
        ]}
        selectable={false}
      >
        {alerts.map((alert, index) => {
          const isResolving = resolvingAlerts.has(alert.id);
          const isDismissing = dismissingAlerts.has(alert.id);
          const isNotifying = notifyingAlerts.has(alert.id);
          const isLoading = isResolving || isDismissing || isNotifying;
          const isPopoverActive = activePopoverId === alert.id;

          return (
              <IndexTable.Row
                key={alert.id}
                id={alert.id.toString()}
                position={index}
                onClick={() => onRowClick?.(alert)}
              >
              <IndexTable.Cell>
                <ProductCell
                  title={alert.product_title || `Variant ${alert.variant_id}`}
                  imageUrl={alert.image_url}
                  variantTitle={alert.variant_title}
                  showTestBadge={hasTestData && isTestProduct(alert.product_title || '')}
                />
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  <Tooltip content={formatFullTimestamp(alert.created_at)}>
                    <BlockStack gap="050">
                      <Text as="span" variant="bodySm" tone="subdued">
                        {currentTime ? formatRelativeTime(alert.created_at, currentTime) : '...'}
                      </Text>
                      {alert.updated_at !== alert.created_at && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          {currentTime ? `Updated ${formatRelativeTime(alert.updated_at, currentTime)}` : '...'}
                        </Text>
                      )}
                    </BlockStack>
                  </Tooltip>
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  <DataPill value={alert.current_inventory} label="units" />
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  <DataPill value={alert.days_until_stockout} label="days" />
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  <DataPill value={Math.round(alert.average_velocity)} label="/day" />
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="center" blockAlign="center">
                  {getSeverityBadge(alert.severity)}
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <InlineStack align="end">
                  {/* Stop propagation to prevent row click when clicking action menu */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <Popover
                      active={isPopoverActive}
                      activator={
                        <Button
                          icon={MenuHorizontalIcon}
                          variant="tertiary"
                          onClick={() => togglePopover(alert.id)}
                          accessibilityLabel="Alert actions"
                          loading={isLoading}
                        />
                      }
                      onClose={closePopover}
                      preferredAlignment="right"
                    >
                      <ActionList
                        items={[
                          {
                            content: 'Mark as resolved',
                            icon: CheckIcon,
                            onAction: () => {
                              closePopover();
                              onResolve(alert);
                            },
                            disabled: isLoading,
                          },
                          {
                            content: 'Dismiss alert',
                            icon: XIcon,
                            onAction: () => {
                              closePopover();
                              onDismiss(alert);
                            },
                            disabled: isLoading,
                          },
                          {
                            content: hasNotificationChannels
                              ? 'Send notification'
                              : 'No channels configured',
                            icon: NotificationIcon,
                            onAction: () => {
                              closePopover();
                              onNotify(alert);
                            },
                            disabled: isLoading || !hasNotificationChannels,
                          },
                        ]}
                      />
                    </Popover>
                  </div>
                </InlineStack>
              </IndexTable.Cell>
            </IndexTable.Row>
          );
        })}
      </IndexTable>
    </div>
  );
}
